import React, { useMemo, useState } from 'react';
import tw, { styled } from 'twin.macro';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Flex, Paragraph, Heading1 } from '@ubisend/pulse-components';

import {
  useDelivery,
  useBot,
  useMutation,
  useQuery
} from '../../../../hooks';
import { groupMessages } from '../../../../utilities';
import { saveMessage, deleteMessage } from '../../../../api';
import Notifications from '../Notifications';
import { MessagePreview } from './Components';
import { Button, ScreenContainer } from '../../Components';

/* -------------------- Styled Components -------------------- */
const HeaderContainer = styled.div`${tw`md:flex w-full justify-between`}`;
const SavedContainer = styled.div`${tw`w-full py-6`}`;
const MessageSearch = styled.input`${tw`text-black w-full md:w-auto px-3 h-8 rounded border-0`} box-sizing: border-box;`;
const Message = styled.div`
  ${tw`md:px-8 md:py-6 px-4 py-3 md:mt-6 mt-4 block text-white no-underline`}
  border-radius: 1rem;
  background-color: rgb(255 255 255 / 0.125);
  border: 2px solid rgb(255 255 255 / 0.125);
  backdrop-filter: blur(8px);
`;
const Timestamp = styled.div`${tw`block w-full text-right mt-3 md:mt-4 opacity-50 font-semibold font-poppins text-xs uppercase tracking-wide whitespace-nowrap`}`;
const MessageActions = styled.div`${tw`flex flex-col md:flex-row`}`;
const ResendButton = styled(Button)`${tw`flex-shrink-0 py-2 mb-2 ml-3 md:mb-0`} & > svg { ${tw`mr-2`} }`;
const SaveButton = styled(ResendButton)`
  & > svg {
    ${props => !props.saved && `
      & path {
        fill: transparent;
        stroke: currentColor;
        stroke-width: 2px;
      }
    `}
  }
`;

/* -------------------- Icons -------------------- */
const BookmarkIcon = () => (
  <svg fill="currentColor" viewBox="0 0 20 20">
    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
  </svg>
);

const ReplyIcon = () => (
  <svg fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

/* -------------------- Utility Functions -------------------- */
const getSubscriberMessages = group => group.some(({ direction }) => direction === 'toServer');

const searchMessageGroup = (messages, search) => {
  if (!search) {
    return true;
  }

  const lowerSearch = search.toLowerCase();
  return messages.some(({ content }) => content?.text?.toLowerCase().includes(lowerSearch));
};

const parseMessageContent = message => {
  try {
    const parsed = typeof message === 'string' ? JSON.parse(message) : message;
    const content = typeof parsed.content === 'string' ? JSON.parse(parsed.content) : parsed.content;
    return content || parsed;
  } catch {
    return typeof message === 'object'
      ? message.content || message
      : { 'text': message };
  }
};

const MessageContent = ({ message }) => {
  const { t } = useTranslation('full_page');
  const { text, latitude, longitude } = parseMessageContent(message);

  if (latitude && longitude) {
    const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}&hl=es;z=14&output=embed`;
    return (
      <>
        {text && <>{text}<br /></>}
        <iframe
          title={t('send_location_iframe_arial_label', { latitude, longitude })}
          width="100%"
          height="250"
          style={{ border: 0, borderRadius: 15 }}
          src={mapUrl}
          allowFullScreen
        />
      </>
    );
  }

  return text || '';
};

MessageContent.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]).isRequired
};

/* -------------------- Saved Messages -------------------- */
const SavedMessages = ({ search = '' }) => {
  const { sendMessage, sendLocationMessage } = useDelivery();
  const { t } = useTranslation('full_page');
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data, isSuccess } = useQuery({
    queryKey: [
      'saved'
    ]
  });

  const mutation = useMutation({
    mutationFn: deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'saved'
        ]
      });
    }
  });

  if (!isSuccess || data.data.length === 0) {
    return null;
  }

  const handleDelete = message => e => {
    e.stopPropagation();
    mutation.mutate(message.id);
  };

  const handleSend = message => e => {
    e.stopPropagation();
    const content = parseMessageContent(message);
    const { text, latitude, longitude } = content;

    latitude && longitude
      ? sendLocationMessage(text, latitude, longitude)
      : sendMessage(text);

    history.push('/window/body/messages');
  };

  return (
    <SavedContainer>
      <h2>{t('messages_group_header')}</h2>
      {data.data
        .filter(msg => searchMessageGroup([{ content: { text: msg.message } }], search))
        .map((message, key) => (
          <Message key={key}>
            <Flex fat between top>
              <Paragraph>{message.name}</Paragraph>
              <Paragraph><MessageContent message={message} /></Paragraph>
              <MessageActions>
                <ResendButton onClick={handleSend(message?.message ? message?.message : message)} disabled={mutation.isDisabled}>
                  <ReplyIcon /> {t('resend_message_button')}
                </ResendButton>
                <SaveButton saved onClick={handleDelete(message)} disabled={mutation.isDisabled}>
                  <BookmarkIcon /> {t('unsave_message_button')}
                </SaveButton>
              </MessageActions>
            </Flex>
            <Timestamp>
              <Notifications.Clock />
              {t('saved_at_label')} {dayjs().to(message.sentAt)}
            </Timestamp>
          </Message>
        ))}
    </SavedContainer>
  );
};

SavedMessages.propTypes = {
  search: PropTypes.string
};

/* -------------------- Message History -------------------- */
const Messages = ({ groups, handleMessageClick, search = '' }) => {
  const { sendMessage, sendLocationMessage, hasChannel } = useDelivery();
  const { t } = useTranslation('full_page');
  const history = useHistory();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: saveMessage,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: [
        'saved'
      ]
    })
  });

  const handleSave = message => e => {
    e.stopPropagation();
    const { text, latitude, longitude } = message.content;
    const messageText = latitude && longitude
      ? `${text || ''}\n${t('send_location_iframe_arial_label', { latitude, longitude })}`
      : text;

    mutation.mutate({
      name: messageText,
      message: JSON.stringify(message.content)
    });
  };

  const handleSend = message => e => {
    e.stopPropagation();
    const { type, content } = message;
    const { text, latitude, longitude } = content;

    type === 'location'
      ? sendLocationMessage(text, latitude, longitude)
      : sendMessage(text);

    history.push('/window/body/messages');
  };

  return groups
    .filter(getSubscriberMessages)
    .filter(group => searchMessageGroup(group, search))
    .map((group, key) => (
      <Message key={key} onClick={() => handleMessageClick(group[0])} style={{ cursor: 'pointer' }}>
        <Flex fat between top>
          <Paragraph><MessageContent message={group[0]} /></Paragraph>
          <MessageActions>
            <ResendButton onClick={handleSend(group[0])} disabled={mutation.isDisabled}>
              <ReplyIcon /> {t('resend_message_button')}
            </ResendButton>
            {hasChannel && (
              <SaveButton onClick={handleSave(group[0])} disabled={mutation.isDisabled}>
                <BookmarkIcon /> {t('save_message_button')}
              </SaveButton>
            )}
          </MessageActions>
        </Flex>
        <Timestamp>
          <Notifications.Clock />
          {t('sent_at_label')} {dayjs().to(group[0].sentAt)}
        </Timestamp>
      </Message>
    ));
};

Messages.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.array).isRequired,
  handleMessageClick: PropTypes.func.isRequired,
  search: PropTypes.string
};

/* -------------------- Main Component -------------------- */
const History = () => {
  const [group, setGroup] = useState(null);
  const [search, setSearch] = useState('');
  const { messages } = useBot();
  const { t } = useTranslation('full_page');

  const groups = useMemo(() => messages.length > 0 && messages.reduce(groupMessages, []), [messages]);

  const handleSearchChange = e => setSearch(e.target.value);

  const handleMessageClick = subscriberMessage => {
    const index = groups.findIndex(messages => messages.some(msg => msg.id === subscriberMessage.id));
    const chatbotResponseGroup = groups.slice(index + 1).find(group => group.some(msg => msg.direction === 'toClient'));
    setGroup(chatbotResponseGroup);
  };

  return group ? (
    <MessagePreview group={group} />
  ) : (
    <ScreenContainer>
      <HeaderContainer>
        <Heading1 initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {t('history_header')}
        </Heading1>
        <MessageSearch
          onChange={handleSearchChange}
          placeholder={t('messages_search_placeholder')}
        />
      </HeaderContainer>
      <SavedMessages search={search} />
      <Messages search={search} groups={groups} handleMessageClick={handleMessageClick} />
    </ScreenContainer>
  );
};

export default History;
